"use client";

import { useState, useMemo, useEffect } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowLeft, Search, X, Loader2 } from "lucide-react";
import type { SubarticuloTransparencia } from "@/lib/api/datosGenerales/types/transparencia";
import {
  GrupoAccordion,
  DocumentosPorAnio,
  FilterDropdown,
} from "../components";

type Documento = {
  id: number;
  titulo: string;
  archivo: string;
  formato: string;
  anio: number;
};

type DocumentosPorAnio = { anio: number; documentos: Documento[] }[];

export default function InformacionFinancieraComponent({
  data,
  transparenciaHref,
}: {
  data: SubarticuloTransparencia;
  transparenciaHref: string;
}) {
  const t = useTranslations("informacionFinancieraPage");
  const tTransparencia = useTranslations("transparenciaPage");
  const { subarticulo } = data;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [isSearching, setIsSearching] = useState(false);
  const [openYears, setOpenYears] = useState<string[]>([]);

  useEffect(() => {
    if (!searchQuery) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => setIsSearching(false), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    subarticulo.grupos.forEach((grupo) =>
      grupo.documentosPorAnio.forEach((docAnio) => years.add(docAnio.anio)),
    );
    subarticulo.documentosSinGrupoPorAnio.forEach((docAnio) =>
      years.add(docAnio.anio),
    );
    return Array.from(years).sort((a, b) => b - a);
  }, [subarticulo]);

  const filtered = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const yearFilter = selectedYear !== "all" ? parseInt(selectedYear) : null;

    const filterPorAnio = (documentosPorAnio: DocumentosPorAnio) =>
      documentosPorAnio
        .filter((docAnio) => yearFilter === null || docAnio.anio === yearFilter)
        .map((docAnio) => ({
          ...docAnio,
          documentos: docAnio.documentos.filter(
            (doc) => query === "" || doc.titulo.toLowerCase().includes(query),
          ),
        }))
        .filter((docAnio) => docAnio.documentos.length > 0);

    return {
      grupos: subarticulo.grupos
        .map((grupo) => ({
          ...grupo,
          documentosPorAnio: filterPorAnio(grupo.documentosPorAnio),
        }))
        .filter((grupo) => grupo.documentosPorAnio.length > 0),
      documentosSinGrupoPorAnio: filterPorAnio(
        subarticulo.documentosSinGrupoPorAnio,
      ),
    };
  }, [subarticulo, searchQuery, selectedYear]);

  const totalDocuments = useMemo(() => {
    let count = 0;
    filtered.grupos.forEach((grupo) =>
      grupo.documentosPorAnio.forEach((docAnio) => {
        count += docAnio.documentos.length;
      }),
    );
    filtered.documentosSinGrupoPorAnio.forEach((docAnio) => {
      count += docAnio.documentos.length;
    });
    return count;
  }, [filtered]);

  const hasActiveFilters = searchQuery !== "" || selectedYear !== "all";

  // Con 17 años colapsados una búsqueda no mostraría nada útil: al filtrar se
  // abren los años que sobreviven, y al limpiar queda expandido sólo el último.
  useEffect(() => {
    if (hasActiveFilters) {
      setOpenYears(
        filtered.documentosSinGrupoPorAnio.map(
          (docAnio) => `anio-${docAnio.anio}`,
        ),
      );
    } else {
      const ultimo = subarticulo.documentosSinGrupoPorAnio[0];
      setOpenYears(ultimo ? [`anio-${ultimo.anio}`] : []);
    }
  }, [hasActiveFilters, filtered, subarticulo]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedYear("all");
  };

  const resultsLabel =
    totalDocuments === 1
      ? tTransparencia("resultsOne", { count: totalDocuments })
      : tTransparencia("resultsMany", { count: totalDocuments });

  const yearOptions = availableYears.map((y) => ({
    value: y.toString(),
    label: y.toString(),
  }));

  const isEmpty =
    filtered.grupos.length === 0 &&
    filtered.documentosSinGrupoPorAnio.length === 0;

  return (
    <div>
      {/* Origen del contenido: deja claro que es el 10.2 de Transparencia */}
      <div className="max-w-3xl mb-12">
        <Link
          href={transparenciaHref}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#102542] hover:gap-3 transition-all mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("backToTransparencia")}
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <span className="shrink-0 text-xs font-mono font-semibold text-[#102542] border border-[#102542]/30 bg-[#102542]/5 px-2 py-0.5 rounded">
            {subarticulo.codigo}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
            {subarticulo.nombre}
          </h2>
        </div>
        <p className="text-gray-600 leading-relaxed">{t("description")}</p>
      </div>

      {/* Filtros */}
      <div className="space-y-4 mb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={tTransparencia("searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-2.5 bg-white border border-gray-200 rounded-full text-sm
                 focus:outline-none focus:border-[#102542] transition-colors text-gray-900 placeholder-gray-400"
            />
            {isSearching && searchQuery && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#102542] animate-spin" />
            )}
            {searchQuery && !isSearching && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                aria-label={tTransparencia("clearFilters")}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {yearOptions.length > 0 && (
            <FilterDropdown
              label={tTransparencia("filterYear")}
              value={selectedYear}
              options={yearOptions}
              onChange={setSelectedYear}
              allLabel={tTransparencia("filterAll")}
            />
          )}
        </div>

        <div className="flex items-center justify-between text-xs uppercase tracking-wider">
          <span className="text-gray-500 font-mono">{resultsLabel}</span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 text-[#102542] font-semibold hover:gap-2 transition-all"
            >
              <X className="w-3.5 h-3.5" />
              {tTransparencia("clearFilters")}
            </button>
          )}
        </div>
      </div>

      {isEmpty ? (
        <div className="text-center py-16">
          <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
            {tTransparencia("noResults")}
          </h3>
          <p className="text-gray-600 mb-6">
            {tTransparencia("noResultsDescription")}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#102542] text-white rounded-full text-sm font-semibold hover:bg-[#0a1a30] transition-colors"
            >
              <X className="w-4 h-4" />
              {tTransparencia("clearFilters")}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Hoy el 10.2 no usa grupos, pero el CMS puede añadirlos */}
          <GrupoAccordion grupos={filtered.grupos} />
          <DocumentosPorAnio
            documentosPorAnio={filtered.documentosSinGrupoPorAnio}
            openYears={openYears}
            onOpenYearsChange={setOpenYears}
          />
        </div>
      )}
    </div>
  );
}
